<template>
  <div class="loading-block" v-if="!this.loadingDone"></div>
  <div class="biogas-overview">
    <div class="row g-3">
      <div
        class="col-sm-12 col-md-6"
        :class="{
          'col-lg-3': column_object.has_chart != 1,
          'col-lg-6': column_object.has_chart === 1,
        }"
        v-for="(column_object, index) in dataOverview"
        v-bind:key="index"
      >
        <div
          class="component-card"
          :class="getStatusCardClass(table.status_data)"
          v-for="(table, tableIdx) in column_object.data"
          v-bind:key="table.index"
        >
          <div
            class="component-card__header"
            :class="{
              'component-card__header--collapsed': isMobile && !isExpanded(index + '_' + tableIdx),
            }"
            @click="isMobile && toggleCard(index + '_' + tableIdx)"
          >
            <div class="component-card__title">
              <span
                class="status-dot"
                :class="getStatusDotClass(table.status_data)"
                v-if="table.status_data || table.status_data == '0'"
              ></span>
              <strong>{{ $t(table.name) }}</strong>
            </div>
            <div class="component-card__meta">
              <span
                class="badge"
                :class="getStatusBadgeClass(table.status_data)"
                v-if="table.status_data || table.status_data == '0'"
                >{{ getStatusLabel(table.status_data) }}</span
              >
              <span class="component-card__time" v-if="table.time_update">
                {{ getTimeFormatted(table.time_update) }}
              </span>
              <i
                v-if="isMobile"
                class="fa-solid fa-chevron-down component-card__chevron"
                :class="{ 'component-card__chevron--open': isExpanded(index + '_' + tableIdx) }"
              ></i>
            </div>
          </div>

          <template v-if="!isMobile || isExpanded(index + '_' + tableIdx)">
            <template v-if="table.has_charts == 1">
              <div class="row g-0">
                <div class="col-sm-12 col-md-6">
                  <div
                    class="component-card__rows"
                    v-if="table.data.filter((row) => row.data_type !== 'chart').length > 0"
                  >
                    <div
                      class="metric-row"
                      v-for="table_row in table.data.filter((row) => row.data_type !== 'chart')"
                      v-bind:key="table_row.index"
                      :class="{ 'metric-row--alert': isLimitReached(table_row) }"
                      v-tooltip
                      :title="getTooltip(table_row)"
                    >
                      <span class="metric-row__name">{{ table_row.name }}</span>
                      <span class="metric-row__value">
                        <template v-if="table_row.data_type == 'datetime'">{{
                          getDateTimeFormatted(table_row.data)
                        }}</template>
                        <template v-else>{{ table_row.data }}</template>
                        <span class="metric-row__unit" v-if="table_row.unit">{{
                          table_row.unit
                        }}</span>
                        <i class="fa-solid fa-down-long" v-if="isLimitLowReached(table_row)"></i>
                        <i class="fa-solid fa-up-long" v-if="isLimitUpperReached(table_row)"></i>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="col-sm-12 col-md-6">
                  <template
                    v-for="table_row in table.data.filter((row) => row.data_type == 'chart')"
                    v-bind:key="table_row.index"
                  >
                    <ApexChart
                      :chartData="table_row.data"
                      :chartTitle="table_row.name"
                      :chartUnit="table_row.unit"
                      :valueField="table_row.value_field"
                      :height="80"
                      :width="400"
                    ></ApexChart>
                  </template>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="component-card__rows">
                <template v-for="table_row in table.data" v-bind:key="table_row.index">
                  <div
                    v-if="table_row.alarm_message_object"
                    class="metric-row"
                    :class="{ 'metric-row--alert': isLimitReached(table_row) }"
                    v-tooltip
                    :title="getTooltip(table_row)"
                  >
                    <span class="metric-row__name">
                      <template v-if="table_row.alarm_message_object.component_name"
                        >{{ table_row.alarm_message_object.component_name }}:
                      </template>
                      {{ table_row.name }}
                    </span>
                    <span class="metric-row__value">
                      <template v-if="table_row.data_type == 'datetime'">{{
                        getDateTimeFormatted(table_row.data)
                      }}</template>
                      <template v-else>{{ table_row.data }}</template>
                    </span>
                  </div>
                  <div
                    v-else-if="table_row.data_type != 'chart'"
                    class="metric-row"
                    :class="{ 'metric-row--alert': isLimitReached(table_row) }"
                    v-tooltip
                    :title="getTooltip(table_row)"
                  >
                    <span class="metric-row__name">{{ table_row.name }}</span>
                    <span class="metric-row__value">
                      <template v-if="table_row.data_type == 'datetime'">{{
                        getDateTimeFormatted(table_row.data)
                      }}</template>
                      <template v-else>{{ table_row.data }}</template>
                      <span class="metric-row__unit" v-if="table_row.unit">{{
                        table_row.unit
                      }}</span>
                      <i class="fa-solid fa-down-long" v-if="isLimitLowReached(table_row)"></i>
                      <i class="fa-solid fa-up-long" v-if="isLimitUpperReached(table_row)"></i>
                    </span>
                  </div>
                  <div v-else-if="table_row.data_type == 'chart'" class="px-2 pt-2 pb-4">
                    <ApexChart
                      :chartData="table_row.data"
                      :chartUnit="table_row.unit"
                      :chartTitle="table_row.name"
                      :valueField="table_row.value_field"
                      :height="80"
                      :width="400"
                    ></ApexChart>
                  </div>
                </template>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import apiClient from '@/services/apiClient';
import ApexChart from '@/components/charts/ApexChart.vue';
const fileDownload = require('js-file-download');
export default {
  name: 'BiogasOverview',
  emits: ['closeModal', 'updateTime'],
  props: {
    plant: {
      type: Object,
      required: true,
    },
  },
  components: {
    ApexChart,
  },
  data() {
    return {
      dataOverview: '',
      loadingDone: true,
      polling: null,
      isStartup: true,
      loading: false,
      isMobile: window.innerWidth < 992,
      expandedCards: {},
    };
  },
  mounted() {
    this._resizeHandler = () => {
      this.isMobile = window.innerWidth < 992;
    };
    window.addEventListener('resize', this._resizeHandler);
    this.pollData();
  },
  updated() {},
  unmounted() {
    console.log('component unmounted');
    clearInterval(this.polling);
    window.removeEventListener('resize', this._resizeHandler);
  },
  methods: {
    toggleCard(key) {
      this.expandedCards[key] = !this.expandedCards[key];
    },
    isExpanded(key) {
      return !!this.expandedCards[key];
    },
    getTooltip(table_row) {
      return this.getDateTimeFormatted(table_row.data_time);
    },
    pollData() {
      this.polling = setInterval(() => {
        console.log('timer hat gefeuert. erstmal schlafen');
        setTimeout(() => {
          this.getDataForPlant();
        }, 2000);
      }, process.env.VUE_APP_REFRESH_INTERVALL_OVERVIEW);

      if (this.isStartup) {
        this.getDataForPlant();
        this.isStartup = false;
      }
    },
    async getDataForPlant() {
      console.log('entry getDataForPlant: ' + this.plant.name);

      var formData = {};
      this.loadingDone = false;
      formData.type_data = 'plain';
      try {
        const response_overview = await apiClient.post(
          `/api/v1/plants/${this.plant.id}/get_data_for_overview/`,
          formData
        );
        console.log(response_overview.data);
        this.processData(response_overview.data);
        this.loadingDone = true;
      } catch (error) {
        console.info('Fehler beim Laden von dashboard');

        console.log(JSON.stringify(error));
      }

      console.log('exit getDataForPlant');
    },
    getDataForPlantExcel() {
      console.log('entry getDataForPlant: ' + this.plant.name);

      var formData = {};

      formData.type_data = 'xlsx';
      axios
        .post(`/api/v1/plants/${this.plant.id}/get_data_for_overview/`, formData, {
          responseType: 'blob',
        })
        .then((response) => {
          console.log('fertig');
          fileDownload(response.data, `${this.plant.name}_Auswertung.xlsx`);
        })
        .catch((error) => {
          console.log(JSON.stringify(error));
        });
      console.log('exit getDataForPlant');
    },
    getDataForPlantPdf() {
      console.log('entry getDataForPlant: ' + this.plant.name);

      var formData = {};

      formData.type_data = 'pdf';
      axios
        .post(`/api/v1/plants/${this.plant.id}/get_data_for_overview/`, formData, {
          responseType: 'blob',
        })
        .then((response) => {
          console.log('fertig');
          fileDownload(response.data, `${this.plant.name}_Auswertung.pdf`);
        })
        .catch((error) => {
          console.log(JSON.stringify(error));
        });
      console.log('exit getDataForPlant');
    },
    processData(data) {
      console.log('entry processData');
      console.log(data);

      let updatedTime = new Date(data.time_update);
      console.log('nach updatedTime');
      this.$emit(
        'updateTime',
        (updatedTime.getHours() < 10 ? '0' : '') +
          updatedTime.getHours() +
          ':' +
          (updatedTime.getMinutes() < 10 ? '0' : '') +
          updatedTime.getMinutes()
      );
      console.log(data.data);
      this.dataOverview = data.data;
      console.log('exit processData');
    },
    getStatusCardClass(status_data) {
      if (status_data === null || status_data === undefined || status_data === '') return '';
      const s = String(status_data);
      if (s === '0') return 'component-card--ok';
      if (s === '1') return 'component-card--warning';
      if (s === '2') return 'component-card--error';
      return '';
    },
    getStatusDotClass(status_data) {
      const s = String(status_data);
      if (s === '0') return 'status-dot--ok';
      if (s === '1') return 'status-dot--warning';
      if (s === '2') return 'status-dot--error';
      return 'status-dot--neutral';
    },
    getStatusBadgeClass(status_data) {
      const s = String(status_data);
      if (s === '0') return 'bg-success';
      if (s === '1') return 'bg-warning text-dark';
      if (s === '2') return 'bg-danger';
      return 'bg-secondary';
    },
    getStatusLabel(status_data) {
      const s = String(status_data);
      if (s === '0') return 'OK';
      if (s === '1') return 'Warnung';
      if (s === '2') return 'Fehler';
      return '';
    },
    isLimitLowReached(table_row) {
      if (!table_row.limit_low) return false;

      return Number(table_row.data) <= Number(table_row.limit_low);
    },
    isLimitUpperReached(table_row) {
      if (!table_row.limit_upper) return false;
      return Number(table_row.data) >= Number(table_row.limit_upper);
    },
    isLimitReached(table_row) {
      if (!table_row.limit_low || !table_row.limit_upper) {
        return false;
      }
      return (
        Number(table_row.data) <= Number(table_row.limit_low) ||
        Number(table_row.data) >= Number(table_row.limit_upper)
      );
    },
    getDateTimeFormatted(time) {
      if (!time) {
        return '';
      }

      let updatedTime = new Date(time);
      return (
        updatedTime.getDate() +
        '.' +
        (updatedTime.getMonth() + 1) +
        ' ' +
        (updatedTime.getHours() < 10 ? '0' : '') +
        updatedTime.getHours() +
        ':' +
        (updatedTime.getMinutes() < 10 ? '0' : '') +
        updatedTime.getMinutes()
      );
    },
    getTimeFormatted(time) {
      if (!time) {
        return '';
      }

      let updatedTime = new Date(time);
      const options = {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      };
      return updatedTime.toLocaleDateString('de-DE', options).replace(',', '');
    },
  },
  computed: {
    dataOverviewComputed() {
      return this.dataOverview;
    },
  },
};
</script>

<style scoped>
.component-card {
  border: 1px solid #e0e0e0;
  border-left: 4px solid #9e9e9e;
  border-radius: 8px;
  margin-bottom: 14px;
  background: #fff;
}
.component-card--ok {
  border-left-color: #28a745;
}
.component-card--warning {
  border-left-color: #ffc107;
}
.component-card--error {
  border-left-color: #dc3545;
}

.component-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  border-radius: 4px 4px 0 0;
  gap: 8px;
  flex-wrap: wrap;
}
.component-card__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}
.component-card__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.component-card__time {
  font-size: 11px;
  color: #6c757d;
  white-space: nowrap;
}

.status-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}
.status-dot--ok {
  background-color: #28a745;
}
.status-dot--warning {
  background-color: #ffc107;
}
.status-dot--error {
  background-color: #dc3545;
}
.status-dot--neutral {
  background-color: #9e9e9e;
}

.component-card__rows {
  padding: 2px 0;
}

.metric-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 14px;
  border-bottom: 0.5px solid #f0f0f0;
}
.metric-row:last-child {
  border-bottom: none;
}
.metric-row--alert {
  background-color: #fff8e6;
}

.metric-row__name {
  font-size: 13px;
  color: #495057;
  flex: 1;
}
.metric-row__value {
  font-size: 13px;
  font-weight: 500;
  color: #212529;
  display: flex;
  align-items: center;
  gap: 4px;
  text-align: right;
  flex-shrink: 0;
}
.metric-row__unit {
  font-size: 11px;
  color: #6c757d;
  font-weight: 400;
}

@media (max-width: 991.98px) {
  .component-card__header {
    cursor: pointer;
    user-select: none;
  }
  .component-card__header--collapsed {
    border-bottom: none;
    border-radius: 4px;
  }
}

.component-card__chevron {
  font-size: 11px;
  color: #6c757d;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}
.component-card__chevron--open {
  transform: rotate(180deg);
}
</style>
